import React from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import cardImg1 from "./img/cardContentImg1.jpg";
import cardImg2 from "./img/cardContentImg2.webp";
import cardImg3 from "./img/cardContentImg3.png";
import cardImg4 from "./img/cardContentImg4.gif";
import cardImg5 from "./img/cardContentImg5.jpg";

// Reusable styles
const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#ddeffa",
  },
  innerBox: {
    px: { xs: 2, sm: 4, md: 10 },
    py: { xs: 6, md: 10 },
    width: { xl: "74%", lg: "79%", md: "90%", sm: "90%" },
  },
  heading: {
    textAlign: "center",
    mb: 4,
  },
  title: {
    fontSize: { xs: "38px", md: "48px" },
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: { xs: "18px", md: "22px" },
    color: "text.secondary",
  },
  stepTitle: {
    fontSize: { xs: "24px", md: "28px" },
    fontWeight: "bold",
  },
  stepDescription: {
    fontSize: { xs: "18px", md: "20px" },
    color: "text.secondary",
    paragraph: true,
  },
  button: {
    mt: 2,
    backgroundColor: "#28568f",
    color: "white",
    fontWeight: "bold",
    px: 3,
    py: 1.2,
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": { transform: "scale(1.05)" },
  },
  image: {
    width: "100%",
    height: "auto",
    borderRadius: 2,
    boxShadow: 3,
    mt: { xs: 4, md: 0 },
  },
  stepNumber: {
    px: 3,
    py: 1,
    fontWeight: 600,
    fontSize: "1.5rem",
    color: "#fff",
    mb: "1rem",
    backgroundColor: 'rgb(0, 208, 132)',
    borderRadius: '10px 0',
    width: 'fit-content',
  },
};

// Step data
const steps = [
  {
    img: cardImg1,
    alt: "Flow Builder",
    title: "Unlock the <span style={{ color: '#0047AB' }}>Flow Builder</span>",
    description:
      "Start your 14-day free trial or subscribe to the add-on to access the Flow Builder.",
    buttonText: "Learn More",
  },
  {
    img: cardImg2,
    alt: "Chatbot Setup",
    title:
      "Create a New <span style={{ color: '#0047AB' }}>Chatbot Flow</span>",
    description:
      "Click “Create Flow” inside your dashboard and give your flow a name.",
    reverse: true,
  },
  {
    img: cardImg3,
    alt: "Launch Chatbot",
    title: "Design Your <span style={{ color: '#0047AB' }}>Chatbot</span>",
    description: (
      <ul style={{ paddingLeft: "1.2em", marginBottom: "1em" }}>
        <li style={{ marginBottom: "0.5em" }}>
          Drag and drop elements from the <strong>Content block</strong> to
          design your chatbot.
        </li>
        <li>
          Use elements like <strong>Quick Replies</strong>,{" "}
          <strong>Lists</strong>, <strong>Product Cards</strong> and{" "}
          <strong>API calls</strong> to build your flow.
        </li>
      </ul>
    ),
    buttonText: "Learn More",
  },
  {
    img: cardImg4,
    alt: "Triggers Setup",
    title: "Set Triggers and Conditions",
    description:
      "Add keywords, regex triggers, or connect the bot to template messages or Click-to-WhatsApp Ads.",
    buttonText: "Learn More",
    reverse: true,
  },
  {
    img: cardImg5,
    alt: "Monitor Chatbot",
    title:
      "Save and Enable Your <span style={{ color: '#0047AB' }}>Chatbot</span>",
    description:
      "Once your flow is complete, save your changes and then enable the chatbot to make it go live. Your WhatsApp chatbot is now ready to engage with users!",
    buttonText: "Learn More",
  },
];

// Reusable Step component
const Step = ({ id, img, alt, title, description, buttonText, reverse }) => (
  <Grid
    container
    spacing={6}
    alignItems="center"
    mb={4}
    flexDirection={reverse ? "row-reverse" : "row"}
  >
    <Grid
      item
      xs={12}
      md={6}
      sx={{ pl: reverse ? 0 : { md: 4 }, pr: reverse ? { md: 4 } : 0 }}
    >
      <Box sx={styles.stepNumber}>Step {id + 1}</Box>
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={styles.stepTitle}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <Typography variant="body1" sx={styles.stepDescription}>
        {description}
      </Typography>
      {buttonText && (
        <Button variant="contained" sx={styles.button}>
          {buttonText}
        </Button>
      )}
    </Grid>
    <Grid item xs={12} md={6}>
      <Box component="img" src={img} alt={alt} sx={styles.image} />
    </Grid>
  </Grid>
);

const CardContent = () => {
  return (
    <Box sx={styles.container}>
      <Box sx={styles.innerBox}>
        {/* Top Section Heading */}
        <Box sx={styles.heading}>
          <Typography variant="h4" sx={styles.title} gutterBottom>
            How It Works
          </Typography>
          <Typography variant="subtitle1" sx={styles.subtitle}>
            Simple Steps to Create Your First Chatbot with{" "}
            <span style={{ color: "#28568f", fontWeight: "bold" }}>
              Vertex Suite
            </span>
          </Typography>
        </Box>

        {/* Steps */}
        {steps.map((step, index) => (
          <Step
            key={index}
            id={index}
            img={step.img}
            alt={step.alt}
            title={step.title}
            description={step.description}
            buttonText={step.buttonText}
            reverse={step.reverse}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CardContent;
